import React from 'react'
import {Box, Link, Stack, Typography} from '@mui/material'
import WbOutlineButton from '../../../common/WbOutlineButton'
import WbTextField from '../../../common/WbTextField'
import SkipLogicConditions from './SkipLogicConditions'
import {ITEM_TYPE} from '../../../../utils/constants'
import {destinationOptions, earlierQuestions} from '../../../../utils/skipLogic/destinations'
import {emptyRule, goToKey} from '../../../../utils/skipLogic/rules'
import {isReferenceableType, optionsForItem} from '../../../../utils/skipLogic/conditions'
import {describeRule} from '../../../../utils/skipLogic/summary'

// A human label for a destination item (null = end of survey). Questions use
// their builder-facing text; other item types fall back to a generic name.
const labelForItem = item => {
  if (!item) return 'End of survey'
  if (item.itemType === ITEM_TYPE.CONTENT) {
    return item.content?.headline?.trim() || 'Content screen'
  }
  if (item.itemType === ITEM_TYPE.SKIP_LOGIC) return 'Skip-logic section'
  return (item.staffText || item.text || '').trim() || 'Question'
}

// Keep the rule's current destination visible in the picker even when it is no
// longer a valid forward option (e.g. an item was moved earlier) - the error
// message explains why, but the respondent-facing value should not vanish.
const withCurrent = (options, goTo) => {
  const key = goToKey(goTo)
  if (!key || options.some(o => o.key === key)) return options
  return [...options, {key, label: 'Selected destination (no longer available)', goTo}]
}

// Builder editor for a skip-logic section. Controlled: reads the `rules` object
// and emits a new one via onChange; it never owns state and never validates -
// the parent runs validation at save time and passes any messages in as
// `errors`. The condition ("if") side of each rule is a placeholder here; the
// full condition editor is a later story.
export default function SkipLogicSectionEditor({
  rules,
  onChange,
  survey,
  sectionId,
  sectionIndex,
  errors = [],
}) {
  const update = changes => onChange({...rules, ...changes})
  const errorFor = code => errors.find(e => e.code === code)?.message

  const ruleList = rules?.rules || []
  const otherwise = rules?.otherwise || {next: true}

  // Later questions/content screens + End of survey - the forward-only
  // destinations a rule may use.
  const baseOptions = destinationOptions(survey, sectionId, sectionIndex).map(o => ({
    key: goToKey(o.goTo),
    label: labelForItem(o.item),
    goTo: o.goTo,
  }))
  const otherwiseOptions = [
    {key: 'next', label: 'Continue to the next question', goTo: {next: true}},
    ...baseOptions,
  ]
  // Earlier questions are the only valid subjects for a rule's conditions;
  // ranking and free-text questions cannot be tested. Each subject carries its
  // type and options so the condition editor can offer the right operators and
  // values.
  const subjects = earlierQuestions(survey, sectionId, sectionIndex)
    .filter(item => isReferenceableType(item.questionType))
    .map(item => ({
      itemId: item.itemId,
      questionType: item.questionType,
      label: labelForItem(item),
      options: optionsForItem(item),
    }))

  const setRuleGoTo = (index, key, options) => {
    const opt = options.find(o => o.key === key)
    if (!opt) return
    update({rules: ruleList.map((r, i) => (i === index ? {...r, goTo: opt.goTo} : r))})
  }
  const setRuleWhen = (index, when) =>
    update({rules: ruleList.map((r, i) => (i === index ? {...r, when} : r))})
  const addRule = () => {
    const goTo = baseOptions[0]?.goTo || {end: true}
    update({rules: [...ruleList, {...emptyRule(), goTo}]})
  }
  const removeRule = index => update({rules: ruleList.filter((_, i) => i !== index)})
  const setOtherwise = key => {
    const opt = otherwiseOptions.find(o => o.key === key)
    if (opt) update({otherwise: opt.goTo})
  }

  return (
    <Stack spacing={2} className="skip-logic-section-editor" sx={{p: 2}}>
      <Typography variant="body2" color="text.secondary">
        This section is not shown to respondents. It decides where they go next,
        based on earlier answers.
      </Typography>
      {baseOptions.length <= 1 && (
        <Typography variant="body2" color="text.secondary">
          Add questions or screens after this section, then save, to choose where
          rules can send people.
        </Typography>
      )}
      <Typography variant="body2">
        Rules are checked in order. The first rule that matches decides where the
        respondent goes.
      </Typography>

      {ruleList.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No rules yet. Everyone follows the otherwise route below.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {ruleList.map((rule, i) => {
            const options = withCurrent(baseOptions, rule.goTo)
            const error = errorFor(`rule-goto:${rule.id}`)
            return (
              <Box
                key={rule.id}
                className="skip-logic-rule"
                role="group"
                aria-label={`Rule ${i + 1}`}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{justifyContent: 'space-between', alignItems: 'baseline'}}
                >
                  <Typography variant="body2">
                    <strong>Rule {i + 1}</strong>
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    color="#E84F6B"
                    underline="hover"
                    aria-label={`Remove rule ${i + 1}`}
                    onClick={() => removeRule(i)}
                  >
                    Remove
                  </Link>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{display: 'block', mb: 1, fontStyle: 'italic'}}
                >
                  {describeRule(survey, rule)}
                </Typography>
                <SkipLogicConditions
                  when={rule.when}
                  subjects={subjects}
                  idPrefix={`rule-${rule.id}`}
                  onChange={next => setRuleWhen(i, next)}
                />
                <WbTextField
                  select
                  SelectProps={{native: true}}
                  InputLabelProps={{shrink: true}}
                  id={`rule-goto-${rule.id}`}
                  label={`Rule ${i + 1} goes to`}
                  value={goToKey(rule.goTo)}
                  onChange={e => setRuleGoTo(i, e.target.value, options)}
                  error={!!error}
                  helperText={error}
                >
                  {options.map(o => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </WbTextField>
              </Box>
            )
          })}
        </Stack>
      )}

      <Box>
        <WbOutlineButton CustomOutlineButtonText="Add rule" onClick={addRule} />
      </Box>

      <WbTextField
        select
        SelectProps={{native: true}}
        InputLabelProps={{shrink: true}}
        id="otherwise-goto"
        label="Otherwise go to"
        value={goToKey(otherwise)}
        onChange={e => setOtherwise(e.target.value)}
        error={!!errorFor('otherwise-goto')}
        helperText={errorFor('otherwise-goto')}
      >
        {withCurrent(otherwiseOptions, otherwise).map(o => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </WbTextField>
    </Stack>
  )
}
